import { IWidget } from 'common';
import { createChangeSettings } from '../utils/createChangeSettings';
import {
  QuickLayout,
  layoutSettings,
  getCurrentLayout as getLayout,
  supportsContentSize as supportsSize,
  updateSettingsFromAdvanced as getAdvancedSettings,
} from '../utils/quickSettingsUtils';
import { resolveCarouselTypeSelection } from '../utils/carouselType';

interface UseQuickDesignProps {
  editedWidget: {
    name: string;
    description: string;
    settings: IWidget['settings'];
  };
  setEditedWidget: (widget: any) => void;
}

/**
 * Hook to manage quick design layout selection and application
 */
export const useQuickDesign = ({ editedWidget, setEditedWidget }: UseQuickDesignProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  const getCurrentLayout = (): QuickLayout => {
    return getLayout(editedWidget.settings);
  };

  const applyLayout = (layout: QuickLayout) => {
    const nextSettings = { ...layoutSettings[layout] };

    if (layout === 'carousel' && !editedWidget.settings.carousel_type) {
      nextSettings.carousel_type = resolveCarouselTypeSelection(
        editedWidget.settings.carousel_type,
        editedWidget.settings.shop_this_look_display,
      );
    }

    changeSettings(nextSettings);
  };

  const updateContentSize = (size: number) => {
    changeSettings({ content_size: size });
  };

  const supportsContentSize = (): boolean => {
    return supportsSize(editedWidget.settings);
  };

  const updateThumbnailShape = (shape: IWidget['settings']['thumbnail_shape']) => {
    changeSettings({ thumbnail_shape: shape });
  };

  const updateThumbnailCorners = (corners: IWidget['settings']['thumbnail_corners']) => {
    changeSettings({ thumbnail_corners: corners });
  };

  const updateCarouselType = (carouselType: IWidget['settings']['carousel_type']) => {
    changeSettings({ carousel_type: carouselType });
  };

  const updateSettingsFromAdvanced = (propertyName: string, propertyValue: any): void => {
    const newSettings = getAdvancedSettings(propertyName, propertyValue);
    if (newSettings) {
      changeSettings(newSettings);
    }
  };

  return {
    currentLayout: getCurrentLayout(),
    applyLayout,
    updateContentSize,
    supportsContentSize,
    updateSettingsFromAdvanced,
    updateThumbnailShape,
    updateThumbnailCorners,
    updateCarouselType,
    thumbnailShape: editedWidget.settings.thumbnail_shape,
    thumbnailCorners: editedWidget.settings.thumbnail_corners,
  };
};
